import { useMemo, useState } from "react";
import {
  useAccount,
  useReadContract,
  useWriteContract,
  useWaitForTransactionReceipt,
} from "wagmi";
import { parseEther } from "viem";
import { motion, AnimatePresence } from "framer-motion";
import {
  addresses,
  presaleAbi,
  erc20Abi,
  type PaymentToken,
  tokenAddressFor,
} from "../config/contracts";
import {
  parseStable,
  estimateFanzOut,
  stableToUsdE8,
  ethToUsdE8,
  formatFanz,
} from "../lib/formatBigInt";
import { SectionHeader } from "./Tokenomics";
import { ProgressBar } from "./ProgressBar";
import { AnimatedNumber } from "./AnimatedNumber";

const HAS_PRESALE = addresses.presale !== "0x0000000000000000000000000000000000000000";

const PHASES = [
  { idx: 1, label: "Phase 1 — Genesis", price: 0.0337, raise: 1_685_000 },
  { idx: 2, label: "Phase 2 — Early", price: 0.045, raise: 2_250_000 },
  { idx: 3, label: "Phase 3 — Public", price: 0.06, raise: 3_000_000 },
  { idx: 4, label: "TGE Launch", price: 0.09, raise: 0 },
];

export default function Presale() {
  return (
    <section id="presale" className="relative mx-auto max-w-7xl px-4 py-20 md:px-8 md:py-28">
      <SectionHeader
        eyebrow="Presale"
        title="Get in before the world does."
        sub="Three phases. Linear price ramp. Optional auto-stake on claim. ETH, USDC,
             USDT — all on Base. Slippage controlled, audited code, claim opens at TGE."
      />

      <div className="mt-12 grid grid-cols-1 gap-6 lg:grid-cols-[1fr_1.1fr]">
        <PhaseLadder />
        <BuyWidget />
      </div>

      <UserAllocation />
    </section>
  );
}

function PhaseLadder() {
  return (
    <div className="panel grain p-6 md:p-8">
      <div className="mb-6 flex items-end justify-between gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-gray-300">
            Price Ladder
          </div>
          <h3 className="mt-1 font-display text-3xl text-white">Phases &amp; Pricing</h3>
        </div>
        <div className="hidden text-right md:block">
          <div className="text-[10px] uppercase tracking-widest text-gray-300">Target raise</div>
          <div className="font-mono text-xl text-white">$6.93M</div>
        </div>
      </div>

      <div className="space-y-3">
        {PHASES.map((p, i) => {
          const active = i === 0;
          return (
            <motion.div
              key={p.idx}
              initial={{ opacity: 0, x: -16 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.08, duration: 0.5 }}
              className={`relative overflow-hidden rounded-2xl border p-4 transition-all ${
                active
                  ? "border-accent/50 bg-accent/[0.06] shadow-[0_0_30px_-12px_rgba(192,132,252,0.5)]"
                  : "border-white/[0.07] bg-white/[0.02]"
              }`}
            >
              <div className="flex items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                  <div
                    className={`grid h-9 w-9 place-items-center rounded-lg font-mono text-sm font-bold ${
                      active
                        ? "bg-gradient-to-br from-accent to-accent-deep text-white shadow-magnet"
                        : "bg-white/[0.05] text-gray-300"
                    }`}
                  >
                    {p.idx}
                  </div>
                  <div>
                    <div className="font-medium text-white">{p.label}</div>
                    <div className="text-xs text-gray-300">
                      {active ? (
                        <span className="inline-flex items-center gap-1.5 text-success">
                          <span className="live-dot" /> Live now
                        </span>
                      ) : (
                        <span>Upcoming</span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="text-right">
                  <div className="font-mono text-lg text-white">${p.price.toFixed(4)}</div>
                  <div className="text-[10px] uppercase tracking-widest text-gray-300">per FANZ</div>
                </div>
              </div>
              {active && (
                <div className="mt-3">
                  <ProgressBar pct={12} />
                </div>
              )}
              {/* sweep beam */}
              {active && (
                <div className="pointer-events-none absolute inset-0 overflow-hidden rounded-2xl">
                  <div className="absolute -inset-y-2 -left-1/3 w-1/3 animate-beam bg-gradient-to-r from-transparent via-white/15 to-transparent" />
                </div>
              )}
            </motion.div>
          );
        })}
      </div>

      <div className="mt-6 grid grid-cols-3 gap-3 border-t border-white/[0.06] pt-5">
        <Pill label="Min buy" value="$25" />
        <Pill label="Max buy" value="$50,000" />
        <Pill label="Slippage" value="0–5%" />
      </div>
    </div>
  );
}

function Pill({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-xl border border-white/[0.07] bg-black/30 px-3 py-2 text-center">
      <div className="text-[9px] uppercase tracking-widest text-gray-300">{label}</div>
      <div className="font-mono text-base text-white">{value}</div>
    </div>
  );
}

function BuyWidget() {
  const { address: userAddr, isConnected } = useAccount();
  const [tokenSel, setTokenSel] = useState<PaymentToken>("USDC");
  const [amountStr, setAmountStr] = useState("100");
  const [slippagePct, setSlippagePct] = useState(1);

  const { data: phaseTuple } = useReadContract({
    address: addresses.presale,
    abi: presaleAbi,
    functionName: "activePhase",
    query: { enabled: HAS_PRESALE },
  });
  const { data: ethPrice } = useReadContract({
    address: addresses.presale,
    abi: presaleAbi,
    functionName: "ethUsdPriceE8",
    query: { enabled: HAS_PRESALE && tokenSel === "ETH" },
  });

  const priceE8 = phaseTuple?.[1]?.priceE8 ?? 3_370_000n;
  const stableAddr = tokenAddressFor(tokenSel);

  const { data: allowance, refetch: refetchAllowance } = useReadContract({
    address: stableAddr ?? undefined,
    abi: erc20Abi,
    functionName: "allowance",
    args: userAddr && stableAddr ? [userAddr, addresses.presale] : undefined,
    query: { enabled: !!userAddr && !!stableAddr && tokenSel !== "ETH" },
  });

  const expectedFanz = useMemo(() => {
    if (!amountStr || Number(amountStr) <= 0) return 0n;
    if (tokenSel === "ETH") {
      const wei = parseEther(amountStr);
      return estimateFanzOut(ethToUsdE8(wei, ethPrice ?? 0n), priceE8);
    }
    const amt = parseStable(amountStr);
    return estimateFanzOut(stableToUsdE8(amt), priceE8);
  }, [amountStr, tokenSel, priceE8, ethPrice]);

  const minFanzOut = useMemo(() => {
    if (slippagePct === 0) return 0n;
    return (expectedFanz * BigInt(10000 - Math.round(slippagePct * 100))) / 10000n;
  }, [expectedFanz, slippagePct]);

  const needsApproval = useMemo(() => {
    if (tokenSel === "ETH" || !stableAddr || !amountStr) return false;
    const amt = parseStable(amountStr);
    return (allowance ?? 0n) < amt;
  }, [tokenSel, stableAddr, amountStr, allowance]);

  const { writeContract, data: txHash, isPending, error } = useWriteContract();
  const { isLoading: txLoading, isSuccess: txDone } = useWaitForTransactionReceipt({ hash: txHash });

  const onApprove = () => {
    if (!stableAddr) return;
    writeContract({
      address: stableAddr,
      abi: erc20Abi,
      functionName: "approve",
      args: [addresses.presale, parseStable(amountStr)],
    });
  };

  const onBuy = () => {
    if (!HAS_PRESALE) return;
    if (tokenSel === "ETH") {
      writeContract({
        address: addresses.presale,
        abi: presaleAbi,
        functionName: "buyWithEth",
        args: [minFanzOut],
        value: parseEther(amountStr),
      });
    } else if (stableAddr) {
      writeContract({
        address: addresses.presale,
        abi: presaleAbi,
        functionName: "buyWithStable",
        args: [stableAddr, parseStable(amountStr), minFanzOut],
      });
    }
  };

  const fanzReadable = Number(formatFanz(expectedFanz, 0).replace(/,/g, ""));
  const minReadable = Number(formatFanz(minFanzOut, 0).replace(/,/g, ""));

  return (
    <div className="panel panel-glow p-6 md:p-8">
      <div className="mb-5 flex items-center justify-between gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-gray-300">
            Buy $FANZ
          </div>
          <h3 className="mt-1 font-display text-3xl text-white">
            ${(Number(priceE8) / 1e8).toFixed(4)} <span className="text-gray-300">/ FANZ</span>
          </h3>
        </div>
        <div className="pill text-success">
          <span className="live-dot" />
          Phase 1 Live
        </div>
      </div>

      {/* Token tabs */}
      <div className="mb-4 grid grid-cols-3 gap-2">
        {(["USDC", "USDT", "ETH"] as const).map((t) => {
          const sel = tokenSel === t;
          return (
            <button
              key={t}
              onClick={() => setTokenSel(t)}
              className={`relative overflow-hidden rounded-xl border px-3 py-3 text-sm font-semibold transition-all ${
                sel
                  ? "border-accent bg-accent/[0.08] text-white shadow-[0_0_18px_-6px_rgba(192,132,252,0.6)]"
                  : "border-white/[0.07] bg-white/[0.02] text-gray-300 hover:border-white/20"
              }`}
            >
              <span className="relative z-10">{t}</span>
              {sel && (
                <motion.div
                  layoutId="token-pill"
                  className="absolute inset-0 -z-0 bg-gradient-to-br from-accent/15 to-transparent"
                />
              )}
            </button>
          );
        })}
      </div>

      {/* Amount input */}
      <div className="rounded-2xl border border-white/[0.07] bg-black/40 p-4">
        <div className="flex items-center justify-between text-xs text-gray-300">
          <span>You pay</span>
          <span>{tokenSel}</span>
        </div>
        <div className="mt-1 flex items-center gap-2">
          <input
            value={amountStr}
            onChange={(e) => setAmountStr(e.target.value.replace(/[^0-9.]/g, ""))}
            inputMode="decimal"
            className="w-full bg-transparent font-mono text-3xl font-semibold text-white placeholder:text-white/20 focus:outline-none"
            placeholder={tokenSel === "ETH" ? "0.5" : "100"}
          />
          <div className="grid h-12 w-12 shrink-0 place-items-center rounded-xl border border-white/10 bg-white/[0.04]">
            <TokenGlyph t={tokenSel} />
          </div>
        </div>
      </div>

      {/* Arrow + receive */}
      <div className="my-2 flex justify-center">
        <div className="grid h-8 w-8 place-items-center rounded-full border border-white/10 bg-background">
          <svg className="h-4 w-4 text-accent" viewBox="0 0 20 20" fill="currentColor">
            <path d="M10 4v12M5 11l5 5 5-5" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </div>
      </div>

      <div className="rounded-2xl border border-accent/20 bg-gradient-to-br from-accent/[0.08] to-transparent p-4">
        <div className="flex items-center justify-between text-xs text-gray-300">
          <span>You receive</span>
          <span>$FANZ</span>
        </div>
        <div className="mt-1 flex items-baseline gap-2">
          <span className="font-mono text-3xl font-semibold text-white">
            <AnimatedNumber value={fanzReadable} />
          </span>
          <span className="font-display text-xl text-accent">$FANZ</span>
        </div>
        <div className="mt-1 text-xs text-gray-300">
          Min after slippage ({slippagePct}%):{" "}
          <span className="font-mono text-white/80">
            <AnimatedNumber value={minReadable} />
          </span>
        </div>
      </div>

      {/* Slippage */}
      <div className="mt-4">
        <div className="mb-2 flex items-center justify-between text-xs text-gray-300">
          <span>Slippage tolerance</span>
          <span className="font-mono font-semibold text-white">{slippagePct.toFixed(1)}%</span>
        </div>
        <input
          type="range"
          min={0}
          max={5}
          step={0.5}
          value={slippagePct}
          onChange={(e) => setSlippagePct(Number(e.target.value))}
          className="w-full accent-accent"
        />
      </div>

      {/* CTA */}
      <div className="mt-5">
        {!HAS_PRESALE ? (
          <div className="rounded-xl border border-white/10 bg-white/[0.03] p-4 text-center text-sm text-gray-300">
            <div className="font-medium text-white">Phase 0 · Pre-Deploy</div>
            <div className="mt-1 text-xs">Contracts not yet on Base Sepolia. Connect early to be first in line.</div>
          </div>
        ) : !isConnected ? (
          <div className="rounded-xl border border-white/10 bg-white/[0.03] p-4 text-center text-sm text-gray-300">
            Connect a wallet to continue
          </div>
        ) : needsApproval ? (
          <button onClick={onApprove} disabled={isPending || txLoading} className="btn-primary w-full">
            {isPending || txLoading ? "Approving…" : `Approve ${tokenSel}`}
          </button>
        ) : (
          <button
            onClick={onBuy}
            disabled={isPending || txLoading || expectedFanz === 0n}
            className="btn-primary w-full"
          >
            {isPending || txLoading ? "Confirming…" : `Buy with ${tokenSel}`}
          </button>
        )}
      </div>

      <AnimatePresence>
        {txDone && (
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            className="mt-3 rounded-xl border border-success/30 bg-success/[0.05] p-3 text-sm text-success"
          >
            Confirmed.{" "}
            <button onClick={() => refetchAllowance()} className="underline">
              refresh
            </button>
          </motion.div>
        )}
        {error && (
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            className="mt-3 rounded-xl border border-error/30 bg-error/[0.05] p-3 text-sm text-error/90"
          >
            {error.message.split("\n")[0]}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function TokenGlyph({ t }: { t: PaymentToken }) {
  if (t === "USDC")
    return (
      <span className="grid h-8 w-8 place-items-center rounded-full bg-[#2775ca] font-mono text-[10px] font-bold text-white">
        USDC
      </span>
    );
  if (t === "USDT")
    return (
      <span className="grid h-8 w-8 place-items-center rounded-full bg-[#26a17b] font-mono text-[10px] font-bold text-white">
        USDT
      </span>
    );
  return (
    <span className="grid h-8 w-8 place-items-center rounded-full bg-gradient-to-br from-zinc-200 to-zinc-500 font-mono text-[10px] font-bold text-black">
      ETH
    </span>
  );
}

function UserAllocation() {
  const { address, isConnected } = useAccount();
  const { data: purchased } = useReadContract({
    address: addresses.presale,
    abi: presaleAbi,
    functionName: "purchased",
    args: address ? [address] : undefined,
    query: { enabled: !!address && HAS_PRESALE, refetchInterval: 30_000 },
  });
  const { data: claimed } = useReadContract({
    address: addresses.presale,
    abi: presaleAbi,
    functionName: "claimed",
    args: address ? [address] : undefined,
    query: { enabled: !!address && HAS_PRESALE, refetchInterval: 30_000 },
  });

  const purchasedNum = Number((purchased ?? 0n) / 10n ** 18n);
  const claimedNum = Number((claimed ?? 0n) / 10n ** 18n);
  const claimable = Math.max(0, purchasedNum - claimedNum);

  const milestones = [
    { label: "TGE", pct: 20, ts: "Q3 2026" },
    { label: "Month 3", pct: 20, ts: "+90d" },
    { label: "Month 6", pct: 20, ts: "+180d" },
    { label: "Month 9", pct: 20, ts: "+270d" },
    { label: "Month 12", pct: 20, ts: "+365d" },
  ];

  return (
    <div className="mt-10 panel grain p-6 md:p-8">
      <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-gray-300">
            Your allocation
          </div>
          <h3 className="mt-1 font-display text-3xl text-white">Vesting timeline</h3>
        </div>
        {isConnected ? (
          <div className="flex flex-wrap gap-3">
            <Stat label="Purchased" value={<AnimatedNumber value={purchasedNum} />} suffix="FANZ" />
            <Stat label="Claimed" value={<AnimatedNumber value={claimedNum} />} suffix="FANZ" />
            <Stat label="Claimable" value={<AnimatedNumber value={claimable} />} suffix="FANZ" highlight />
          </div>
        ) : (
          <div className="text-sm text-gray-300">Connect wallet to see your unlocks</div>
        )}
      </div>

      <div className="relative">
        {/* spine */}
        <div className="absolute left-0 right-0 top-1/2 h-px -translate-y-1/2 bg-gradient-to-r from-accent/40 via-magenta/40 to-cyan/40" />

        <div className="relative grid grid-cols-5 gap-2">
          {milestones.map((m, i) => (
            <motion.div
              key={m.label}
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.05 * i, duration: 0.5 }}
              className="relative flex flex-col items-center"
            >
              <div className="font-display text-2xl text-white">{m.pct}%</div>
              <div
                className="my-2 grid h-7 w-7 place-items-center rounded-full border-2 border-accent bg-background"
                style={{ boxShadow: "0 0 14px rgba(192, 132, 252, 0.7)" }}
              >
                <span className="h-1.5 w-1.5 rounded-full bg-accent" />
              </div>
              <div className="text-xs font-medium text-white">{m.label}</div>
              <div className="text-[10px] text-gray-300">{m.ts}</div>
            </motion.div>
          ))}
        </div>
      </div>

      {isConnected && (
        <div className="mt-6 flex flex-col gap-2 border-t border-white/[0.06] pt-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="text-xs text-gray-300">
            Claims open at TGE. You can opt to auto-stake on claim for the 20% APY pool.
          </div>
          <ClaimButton claimable={claimable} />
        </div>
      )}
    </div>
  );
}

function Stat({
  label,
  value,
  suffix,
  highlight,
}: {
  label: string;
  value: React.ReactNode;
  suffix?: string;
  highlight?: boolean;
}) {
  return (
    <div className={`rounded-xl border px-4 py-2 ${highlight ? "border-accent/40 bg-accent/[0.06]" : "border-white/[0.07] bg-white/[0.02]"}`}>
      <div className="text-[10px] uppercase tracking-widest text-gray-300">{label}</div>
      <div className={`font-mono text-base font-semibold ${highlight ? "text-accent" : "text-white"}`}>
        {value} {suffix && <span className="text-xs text-gray-300">{suffix}</span>}
      </div>
    </div>
  );
}

function ClaimButton({ claimable }: { claimable: number }) {
  const { writeContract, isPending, data: hash } = useWriteContract();
  const { isLoading } = useWaitForTransactionReceipt({ hash });
  return (
    <div className="flex flex-wrap gap-2">
      <button
        disabled={!HAS_PRESALE || claimable <= 0 || isPending || isLoading}
        onClick={() =>
          writeContract({
            address: addresses.presale,
            abi: presaleAbi,
            functionName: "claim",
            args: [false],
          })
        }
        className="btn-secondary text-sm"
      >
        Claim
      </button>
      <button
        disabled={!HAS_PRESALE || claimable <= 0 || isPending || isLoading}
        onClick={() =>
          writeContract({
            address: addresses.presale,
            abi: presaleAbi,
            functionName: "claim",
            args: [true],
          })
        }
        className="btn-primary text-sm"
      >
        Claim &amp; Stake
      </button>
    </div>
  );
}
